import axios from "axios";
import Cookies from "js-cookie";

import { getValidAccessToken } from "auth";
import { safeLocalStorage } from "util/safeStore";
import { TaskQueue } from "./taskQueue";

let hasLoaded = false;
let allData: { [key: string]: any } = {};
let loadedDashboardId: string | null | undefined;
let loadedRepairRevision: number | null = null;
let saveConflictNotified = false;
const taskQueue = new TaskQueue();
let saveTimeout: NodeJS.Timeout | null = null;

const BASE_URL = process.env.REACT_APP_BASE_URL;
export const DASHBOARD_SAVE_CONFLICT_EVENT = "digi-dashboard-save-conflict";

const reportSaveError = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 409) {
    console.error("Dashboard save failed:", error);
    return;
  }

  if (!saveConflictNotified) {
    saveConflictNotified = true;
    window.dispatchEvent(new Event(DASHBOARD_SAVE_CONFLICT_EVENT));
  }
};

export const saveData = async (key: string, data: any) => {
  if (saveConflictNotified) return;
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    console.error("No access token available for saveData");
    return;
  }

  try {
    // Ensure allData is always an object
    if (typeof allData !== "object" || allData === null) {
      allData = {};
    }

    // Always update the specific key in allData
    allData[key] = data;
    if (key === "DashboardData" && Array.isArray(data?.dashboardGroups)) {
      allData.global = data;
    }
    // Mirror the write to localStorage: the widgets hook hydrates from it and the
    // reassembly below reads it. safeLocalStorage swallows QuotaExceededError so a
    // local-write failure can't abort the API save.
    safeLocalStorage.setItem(key, JSON.stringify(data));

    // A cached global can predate a CMS repair, so never replay it over the
    // API-loaded state when saving an unrelated key.
    const localStorageKeys = ["WidgetData", "DashboardData"];

    localStorageKeys.forEach((localStorageKey) => {
      const localStorageData = localStorage.getItem(localStorageKey);
      if (localStorageData) {
        try {
          const parsedData = JSON.parse(localStorageData);
          // Ensure allData is an object before assigning
          if (typeof allData !== "object" || allData === null) {
            allData = {};
          }
          allData[localStorageKey] = parsedData;
          console.log(`Updated ${localStorageKey} from localStorage before saving to WordPress:`, {
            dataKeys: Object.keys(parsedData),
            ...(localStorageKey === "WidgetData" && {
              widgetCount: Object.keys(parsedData).length,
            }),
            ...(localStorageKey === "global" && {
              dashboardGroupsCount: parsedData.dashboardGroups?.length || 0,
              activeDashboardId: parsedData.activeDashboardId,
              activeDashboardGroupId: parsedData.activeDashboardGroupId,
            }),
          });
        } catch (parseError) {
          console.error(`Error parsing localStorage ${localStorageKey}:`, parseError);
        }
      }
    });

    const request = () =>
      axios.post(
        `${BASE_URL}/set_interactive_dashboard`,
        {
          ...allData,
          repairRevision: loadedRepairRevision ?? 0,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Debounce the save request by 100ms to prevent rapid successive calls
    saveTimeout = setTimeout(() => {
      taskQueue.pushRequest(request, "Save Request").catch(reportSaveError);
      console.log("Saving data to WordPress:", {
        key,
        data,
        allDataKeys: Object.keys(allData),
        widgetDataCount: Object.keys(allData["WidgetData"] || {}).length,
        dashboardGroupsCount: allData["global"]?.dashboardGroups?.length || 0,
      });
    }, 100);
  } catch (error: any) {
    console.error("Save data error:", error);
  }
};

// Force immediate save without debouncing - used for critical operations like dashboard creation
export const forceSaveData = async (key: string, data: any) => {
  if (saveConflictNotified) return;
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    console.error("No access token available for forceSaveData");
    return;
  }

  try {
    // Ensure allData is always an object
    if (typeof allData !== "object" || allData === null) {
      allData = {};
    }

    // Always update the specific key in allData
    allData[key] = data;
    if (key === "DashboardData" && Array.isArray(data?.dashboardGroups)) {
      allData.global = data;
    }
    // Mirror the write to localStorage (see saveData).
    safeLocalStorage.setItem(key, JSON.stringify(data));

    // Keep the explicit global/DashboardData payload authoritative over local cache.
    const localStorageKeys = ["WidgetData", "DashboardData"];

    localStorageKeys.forEach((localStorageKey) => {
      const localStorageData = localStorage.getItem(localStorageKey);
      if (localStorageData) {
        try {
          const parsedData = JSON.parse(localStorageData);
          // Ensure allData is an object before assigning
          if (typeof allData !== "object" || allData === null) {
            allData = {};
          }
          allData[localStorageKey] = parsedData;
          console.log(
            `Updated ${localStorageKey} from localStorage before force saving to WordPress:`,
            {
              dataKeys: Object.keys(parsedData),
              ...(localStorageKey === "WidgetData" && {
                widgetCount: Object.keys(parsedData).length,
              }),
              ...(localStorageKey === "global" && {
                dashboardGroupsCount: parsedData.dashboardGroups?.length || 0,
                activeDashboardId: parsedData.activeDashboardId,
                activeDashboardGroupId: parsedData.activeDashboardGroupId,
              }),
            },
          );
        } catch (parseError) {
          console.error(`Error parsing localStorage ${localStorageKey}:`, parseError);
        }
      }
    });

    const request = () =>
      axios.post(
        `${BASE_URL}/set_interactive_dashboard`,
        {
          ...allData,
          repairRevision: loadedRepairRevision ?? 0,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Force immediate save without debouncing
    taskQueue.pushRequest(request, "Force Save Request").catch(reportSaveError);
    console.log("Force saving data to WordPress:", {
      key,
      data,
      allDataKeys: Object.keys(allData),
      widgetDataCount: Object.keys(allData["WidgetData"] || {}).length,
      dashboardGroupsCount: allData["global"]?.dashboardGroups?.length || 0,
    });
  } catch (error: any) {
    console.error("Force save data error:", error);
  }
};

export const loadData = async (dataKey: string) => {
  // Throws on transport/HTTP errors (network, 4xx, 5xx, auth). Returns {} only
  // when the request succeeded but the key is absent, so callers can tell a
  // failed fetch apart from a genuinely empty (new user) dashboard.
  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    throw new Error("Unable to obtain a valid access token for loadData");
  }

  const response = await axios.get(`${BASE_URL}/get_interactive_dashboard`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  hasLoaded = true;
  allData = response.data;
  const dashboardId = response.data?._id ?? null;
  if (loadedDashboardId === undefined || dashboardId !== loadedDashboardId) {
    loadedDashboardId = dashboardId;
    const revision = response.data?.repairRevision;
    loadedRepairRevision = Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
  }

  if (!allData || !allData[dataKey]) {
    return {};
  }

  return allData[dataKey];
};

const getUploadUrl = async (fileName: string): Promise<any> => {
  const accessToken = await getValidAccessToken();
  const uniqueFileName = `${Date.now()}-${fileName}`;

  if (!accessToken) {
    throw new Error("No valid access token available for getting upload URL");
  }

  try {
    const response = await axios.get(`${BASE_URL}/upload_url`, {
      params: {
        isPublic: true,
        key: uniqueFileName,
        contentType: "image/jpeg",
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    console.log("Upload URL response:", response.data);

    if (!response.data || !response.data.uploadUrl) {
      throw new Error("Invalid response from upload URL endpoint");
    }

    return response.data;
  } catch (error) {
    console.error("Failed to get upload URL:", error);
    throw error;
  }
};

const uploadFileToS3 = async (file: any, data: any) => {
  if (!data || !data.uploadUrl) {
    throw new Error("Invalid upload data - missing upload URL");
  }

  try {
    await axios.put(data.uploadUrl, file, {
      headers: {
        "Content-Type": file.type || "image/jpeg",
      },
    });
  } catch (error) {
    console.error("Upload to S3 failed:", error);
    throw error;
  }
};

const uploadImage = async (file: any): Promise<any> => {
  if (!file) {
    throw new Error("No file provided for upload");
  }

  if (!file.name) {
    throw new Error("File name is required");
  }
  const data = await getUploadUrl(file.name);

  if (!data) {
    throw new Error("Failed to get upload URL");
  }

  await uploadFileToS3(file, data);
  return data?.fetchUrl?.toString();
  // Adjust the URL as needed
};

export const handleImageUpload = async (file: any) => {
  try {
    const result = await uploadImage(file);
    if (!result) {
      throw new Error("Upload returned empty result");
    }
    return result;
  } catch (error) {
    console.error("Image upload failed:", error);
    throw error; // Re-throw the error so the calling function can handle it
  }
};
